/**
 * Cart Calculations (Layer 0)
 * 
 * 职责：纯数学计算
 * - 乐观更新总价/数量计算
 * - 折扣判定
 */

import type { CartItem, CartSummary, DiscountApplication, FreeShippingProgress } from '../types/cartTypes';
import { formatCurrency } from '../constants/i18n';
import { isFreeShipping } from './cartRules';

/**
 * 判定是否存在折扣
 * @param originalPrice - 原始价格 (分)
 * @param finalPrice - 最终价格 (分)
 * @returns 是否有折扣
 */
export function hasDiscount(originalPrice: number, finalPrice: number): boolean {
    return originalPrice > finalPrice;
}


/**
 * 衍生免邮进度信息
 * @param totalCents - 当前总价 (分)
 * @param thresholdCents - 免邮阈值 (分)
 * @returns 免邮进度对象
 */
export function deriveFreeShippingProgress(totalCents: number, thresholdCents: number): FreeShippingProgress {
    const progress = thresholdCents > 0 ? Math.min(1, totalCents / thresholdCents) : 0;

    return {
        progress,
        progressPercent: Math.round(progress * 100),
        isFreeShipping: isFreeShipping(totalCents, thresholdCents),
        remaining: Math.max(0, thresholdCents - totalCents),
    };
}


/**
 * 构建购物车摘要对象，用于 UI 显示格式化后的状态
 * @param params - 基础价格和数量参数
 * @returns 购物车摘要对象
 */
export function buildCartSummary(params: {
    totalPriceCents: number;
    originalTotalPriceCents: number;
    totalDiscountCents: number;
    itemCount: number;
    currency: string;
}): CartSummary {
    const { totalPriceCents, originalTotalPriceCents, totalDiscountCents, itemCount, currency } = params;

    return {
        subtotal: formatCurrency(originalTotalPriceCents, currency),
        total: formatCurrency(totalPriceCents, currency),
        totalDiscount: formatCurrency(totalDiscountCents, currency),
        itemCount,
        currency,
        originalTotal: formatCurrency(originalTotalPriceCents, currency),
        hasDiscount: totalDiscountCents > 0,
    };
}


/**
 * 计算乐观状态下单行项目的价格汇总
 * @param item - 原始行项目
 * @param optimisticQuantity - 乐观修改后的数量
 * @returns 包含行总价、原始总价及折扣状态的对象
 */
export function calculateOptimisticLinePrices(item: CartItem, optimisticQuantity: number) {
    const safeQuantity = Math.max(0, optimisticQuantity);
    if (safeQuantity === 0) {
        return {
            finalLinePrice: 0,
            originalLinePrice: 0,
            hasDiscount: false
        };
    }

    const baseQuantity = item.quantity || 0;
    const unitFinalPrice = baseQuantity > 0
        ? item.final_line_price / baseQuantity
        : item.price;
    const unitOriginalPrice = baseQuantity > 0
        ? item.original_line_price / baseQuantity
        : item.original_price;

    const finalLinePrice = Math.round(unitFinalPrice * safeQuantity);
    const originalLinePrice = Math.round(unitOriginalPrice * safeQuantity);

    return {
        finalLinePrice,
        originalLinePrice,
        hasDiscount: originalLinePrice > finalLinePrice
    };
}

/**
 * 基于行项目计算乐观态总价/数量
 * @param items - 当前 UI 行项目
 * @returns 乐观态汇总
 */
export function calculateOptimisticCartTotals(
    items: CartItem[],
    cartLevelDiscounts: DiscountApplication[] = []
) {
    let totalPrice = 0;
    let originalTotalPrice = 0;
    let count = 0;

    items.forEach((item) => {
        const qty = Math.max(0, item.quantity || 0);
        count += qty;
        const { finalLinePrice, originalLinePrice } = calculateOptimisticLinePrices(item, qty);
        totalPrice += finalLinePrice;
        originalTotalPrice += originalLinePrice;
    });

    const cartLevelDiscountTotal = cartLevelDiscounts.reduce(
        (sum, discount) => sum + (discount.total_allocated_amount || 0),
        0
    );
    totalPrice = Math.max(0, totalPrice - cartLevelDiscountTotal);
    const totalDiscount = Math.max(0, originalTotalPrice - totalPrice);

    return {
        count,
        total_price: totalPrice,
        original_total_price: originalTotalPrice,
        total_discount: totalDiscount
    };
}
